import type { SpotDto } from "@boomap/contracts";
import { useCallback, useEffect, useRef, useState } from "react";

import { useI18n } from "../i18n/index.ts";
import { apiErrorMessage } from "./api-error-message.ts";
import { listSpots } from "./api-client.ts";
import type { MapArea } from "./types.ts";

/** La carte émet un `moveend` par geste ; on laisse retomber la poussière. */
const DEBOUNCE_IN_MS = 250;

interface SpotsState {
  readonly spots: readonly SpotDto[];
  readonly truncated: boolean;
  /** La fenêtre est trop large pour être interrogée : inviter à zoomer, sans alerte. */
  readonly zoomedOut: boolean;
  readonly loading: boolean;
  readonly error: string | null;
}

export interface UseSpotsResult extends SpotsState {
  readonly setArea: (area: MapArea) => void;
  readonly refresh: () => void;
  readonly upsert: (spot: SpotDto) => void;
  readonly remove: (spotId: string) => void;
}

/**
 * Charge les pastilles de la fenêtre courante. Une requête en vol est annulée
 * dès que la carte bouge à nouveau : sans cela, un déplacement rapide fait
 * arriver les réponses dans le désordre et la carte clignote.
 */
export const useSpots = (): UseSpotsResult => {
  const { t } = useI18n();
  const [area, setArea] = useState<MapArea | null>(null);
  const [state, setState] = useState<SpotsState>({
    spots: [],
    truncated: false,
    zoomedOut: false,
    loading: false,
    error: null,
  });

  const requestRef = useRef<AbortController | null>(null);

  const load = useCallback(
    async (target: MapArea): Promise<void> => {
      requestRef.current?.abort();
      const controller = new AbortController();
      requestRef.current = controller;

      setState((previous) => ({ ...previous, loading: true, error: null }));

      try {
        const result = await listSpots(target, controller.signal);

        setState({
          spots: result.spots,
          truncated: result.truncated,
          zoomedOut: result.zoomedOut,
          loading: false,
          error: null,
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setState((previous) => ({
          ...previous,
          loading: false,
          error: apiErrorMessage(error, t, t.errors.spotsUnavailable),
        }));
      }
    },
    [t],
  );

  useEffect(() => {
    if (area === null) {
      return undefined;
    }

    const timer = setTimeout(() => void load(area), DEBOUNCE_IN_MS);

    return () => clearTimeout(timer);
  }, [area, load]);

  useEffect(() => () => requestRef.current?.abort(), []);

  return {
    ...state,
    setArea,
    refresh: useCallback(() => {
      if (area !== null) {
        void load(area);
      }
    }, [area, load]),
    upsert: useCallback((spot: SpotDto) => {
      setState((previous) => ({
        ...previous,
        spots: [spot, ...previous.spots.filter((existing) => existing.id !== spot.id)],
      }));
    }, []),
    remove: useCallback((spotId: string) => {
      setState((previous) => ({
        ...previous,
        spots: previous.spots.filter((existing) => existing.id !== spotId),
      }));
    }, []),
  };
};
